/* Post-auth destination intent, persisted across the email-verification detour.
 *
 * Sign-up wants a completed account to land on the first-run wizard (/welcome).
 * That intent normally rides in the URL (?next=welcome), but email verification
 * can complete in a context that re-enters the app WITHOUT that query (the
 * verify link's continueUrl carries no `next`). localStorage (not session) so
 * the intent also survives a fresh tab opened from the email. Best-effort:
 * storage can throw (private mode / disabled), in which case we degrade to the
 * URL-only behavior. Consumed once, then cleared, so a later plain login can't
 * inherit a stale sign-up intent. */

const KEY = "igw.postAuthIntent";
const WELCOME = "welcome";
const INVITATION_KEY = "igw.pendingInvitation";

export function markWelcomeIntent(): void {
  try {
    window.localStorage.setItem(KEY, WELCOME);
  } catch {
    // Best effort only — falls back to the ?next=welcome URL param.
  }
}

export function hasWelcomeIntent(): boolean {
  try {
    return window.localStorage.getItem(KEY) === WELCOME;
  } catch {
    return false;
  }
}

export function clearPostAuthIntent(): void {
  try {
    window.localStorage.removeItem(KEY);
    window.localStorage.removeItem(INVITATION_KEY);
  } catch {
    // Best effort only.
  }
}

/* Pending invitation id, stashed so it can be removed from the URL.
 *
 * An invite deeplink puts a capability token in the query string
 * (/signup?invitation_id=…) — that id alone accepts the org invitation. The
 * sign-up conversion event fires while the browser sits on that URL, and a GTM
 * tag reading the built-in Page URL variable would forward the token to a third
 * party. So the sign-up screen stashes it here and strips it from the URL;
 * nextAfterAuth reads this when the query no longer carries one.
 *
 * Storage-backed rather than in-memory because the social path leaves the page
 * entirely (signInWithRedirect) and the id must survive the round trip —
 * socialLogin never consumes it, only the /invite/:id hop does. Same best-effort
 * + consumed-once contract as the welcome intent above; clearPostAuthIntent
 * drops both. */
/** Returns whether the id was actually stored. Callers MUST NOT strip the URL
 *  param on a false return — storage can throw (private mode / disabled), and
 *  removing the only other copy would lose the invitation entirely. */
export function markPendingInvitation(invitationId: string): boolean {
  try {
    window.localStorage.setItem(INVITATION_KEY, invitationId);
    return true;
  } catch {
    return false;
  }
}

export function readPendingInvitation(): string | null {
  try {
    return window.localStorage.getItem(INVITATION_KEY);
  } catch {
    return null;
  }
}
